import { useCallback, useState } from 'react';
import {
  getInProgressResponsesForSurvey,
  SurveyMatchTarget,
} from '../util/getInProgressResponsesForSurvey';

export type StartSurveyOptions = {
  openInNewTab?: boolean;
  skipInProgressPrompt?: boolean;
};

export type UseInProgressSurveyStartArgs<TSurvey extends SurveyMatchTarget> = {
  responses: fhir.QuestionnaireResponse[] | undefined;
  areResponsesReady?: boolean;
  onStartNew: (survey: TSurvey, options?: StartSurveyOptions) => void;
  onContinue: (
    survey: TSurvey,
    response: fhir.QuestionnaireResponse,
    options?: StartSurveyOptions,
  ) => void;
};

export type UseInProgressSurveyStartResult<TSurvey extends SurveyMatchTarget> =
  {
    isPromptOpen: boolean;
    pendingDrafts: fhir.QuestionnaireResponse[];
    requestStart: (survey: TSurvey, options?: StartSurveyOptions) => void;
    dismiss: () => void;
    confirmContinue: (response: fhir.QuestionnaireResponse) => void;
    confirmStartNew: () => void;
  };

export function useInProgressSurveyStart<TSurvey extends SurveyMatchTarget>({
  responses,
  areResponsesReady = true,
  onStartNew,
  onContinue,
}: UseInProgressSurveyStartArgs<TSurvey>): UseInProgressSurveyStartResult<TSurvey> {
  const [pending, setPending] = useState<{
    survey: TSurvey;
    drafts: fhir.QuestionnaireResponse[];
    options?: StartSurveyOptions;
  } | null>(null);

  const requestStart = useCallback(
    (survey: TSurvey, options?: StartSurveyOptions) => {
      if (!areResponsesReady) {
        return;
      }
      const drafts = getInProgressResponsesForSurvey(responses, survey);
      if (drafts.length === 0) {
        onStartNew(survey, options);
        return;
      }
      setPending({ survey, drafts, options });
    },
    [areResponsesReady, onStartNew, responses],
  );

  const dismiss = useCallback(() => {
    setPending(null);
  }, []);

  const confirmContinue = useCallback(
    (response: fhir.QuestionnaireResponse) => {
      if (!pending) {
        return;
      }
      const { survey, options } = pending;
      setPending(null);
      onContinue(survey, response, options);
    },
    [onContinue, pending],
  );

  const confirmStartNew = useCallback(() => {
    if (!pending) {
      return;
    }
    const { survey, options } = pending;
    setPending(null);
    onStartNew(survey, {
      ...(options ?? {}),
      skipInProgressPrompt: true,
    });
  }, [onStartNew, pending]);

  return {
    isPromptOpen: pending !== null,
    pendingDrafts: pending?.drafts ?? [],
    requestStart,
    dismiss,
    confirmContinue,
    confirmStartNew,
  };
}
